import { useCallback, useEffect, useState } from 'react';
import { Skeleton, Toggle } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { ApiError } from '@/lib/api-client';
import { productMasterService, type MasterOutlet, type MasterWithOutlets } from '@/services/product-master.service';

/**
 * Where a product is sold, one row per location (docs/master-catalogue-plan.md
 * §17, R6): on or off at each location, and — in the `menu` form, for a
 * restaurant dish — whether it is available or sold out (86) at a location
 * that sells it. The same API as the admin Stores matrix; admin components cannot be
 * imported here, so this is its own small list. An Owner may change every
 * location, a Manager only assigned ones — the API checks the same rule.
 */
export function ProductLocationsPanel({ productId, menu = false }: { productId: string; menu?: boolean }) {
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const isOwner = user?.role === 'OWNER';
  const assigned = new Set((profile?.locations ?? []).map((l) => l.id));
  const canManage = (locationId: string) => isOwner || assigned.has(locationId);
  const onLabel = menu ? 'On the menu' : 'Sold here';

  const [master, setMaster] = useState<MasterWithOutlets | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'none' | 'error'>('loading');
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);

  const load = useCallback(async () => {
    try {
      const masterId = await productMasterService.masterIdOf(productId);
      if (!masterId) {
        setState('none');
        return;
      }
      setMaster(await productMasterService.getById(masterId));
      setState('ready');
    } catch {
      setState('error');
    }
  }, [productId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function change(outlet: MasterOutlet, run: () => Promise<unknown>, done: string) {
    setBusy(outlet.location_id);
    setMessage(null);
    try {
      await run();
      setMessage({ text: done, ok: true });
      await load();
    } catch (err) {
      setMessage({ text: err instanceof ApiError ? err.message : 'Could not change this location', ok: false });
    } finally {
      setBusy(null);
    }
  }

  if (state === 'none') return null;

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden animate-fade-in">
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gradient-to-r from-violet-50 to-pink-50 dark:from-violet-900/20 dark:to-pink-900/10 border-b border-neutral-100 dark:border-neutral-700">
        <h2 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Locations</h2>
        {master && (
          <span className="inline-flex items-center rounded-full bg-violet-100 dark:bg-violet-900/30 px-2.5 py-0.5 text-xs font-semibold text-violet-700 dark:text-violet-300">
            On at {master.stores.filter((s) => s.enabled).length} / {master.stores.length}
          </span>
        )}
      </div>

      {state === 'loading' && (
        <div className="p-4 space-y-2">
          <Skeleton variant="rect" width="100%" height={40} />
          <Skeleton variant="rect" width="100%" height={40} />
        </div>
      )}
      {state === 'error' && (
        <p className="p-4 text-sm text-danger-600 dark:text-danger-400">Could not load the locations.</p>
      )}

      {state === 'ready' && master && (
        <ul role="list" className="divide-y divide-neutral-100 dark:divide-neutral-700">
          {master.stores.map((outlet) => {
            const manage = canManage(outlet.location_id);
            const available = outlet.is_available !== false;
            const working = busy === outlet.location_id;
            return (
              <li
                key={outlet.location_id}
                data-testid={`outlet-row-${outlet.location_id}`}
                className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 transition-all duration-200 even:bg-neutral-50 hover:bg-violet-50 dark:even:bg-neutral-800/60 dark:hover:bg-violet-900/20"
              >
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  {outlet.location_name}
                </span>
                {manage ? (
                  <Toggle
                    size="sm"
                    checked={outlet.enabled}
                    disabled={working}
                    label={outlet.enabled ? onLabel : 'Not sold'}
                    onChange={(on) =>
                      change(
                        outlet,
                        () => productMasterService.setLocations(master.id, on ? { enable: [outlet.location_id] } : { disable: [outlet.location_id] }),
                        on ? `Now sold at ${outlet.location_name}` : `No longer sold at ${outlet.location_name}`,
                      )
                    }
                  />
                ) : (
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">{outlet.enabled ? onLabel : 'Not sold'}</span>
                )}
                {menu && outlet.enabled && outlet.product_id && (
                  manage ? (
                    <Toggle
                      size="sm"
                      checked={available}
                      disabled={working}
                      label={available ? 'Available' : 'Sold out'}
                      onChange={(next) =>
                        change(
                          outlet,
                          () => productMasterService.setMenuAvailability(outlet.product_id as string, next),
                          next ? `Available again at ${outlet.location_name}` : `Sold out at ${outlet.location_name}`,
                        )
                      }
                    />
                  ) : (
                    <span
                      className={
                        available
                          ? 'rounded-full bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-700 dark:bg-teal-900/30 dark:text-teal-300'
                          : 'rounded-full bg-danger-100 px-2 py-0.5 text-xs font-medium text-danger-700 dark:bg-danger-900/30 dark:text-danger-300'
                      }
                    >
                      {available ? 'Available' : 'Sold out'}
                    </span>
                  )
                )}
              </li>
            );
          })}
        </ul>
      )}

      {message && (
        <p
          role="status"
          className={`px-4 py-2 text-xs border-t border-neutral-100 dark:border-neutral-700 ${message.ok ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'}`}
        >
          {message.text}
        </p>
      )}
    </div>
  );
}
